// 01 / MACHINE — three tabs:
//
//   • Statler:   3 throat sizes (22/26/30) + an "Upgrade to Ascend" toggle
//                that swaps the selection (and pricing) to the Ascend tier.
//   • Vision:    4 throat sizes (18/22/26/30).
//   • Upgrades:  Ascend Retrofit / Ascend Upgrade — for customers who
//                already own a Gammill and want to upgrade it rather than
//                buy a new one.
//
// selectedMachineId is a single radio across ALL tabs — picking a Vision
// after picking a Statler replaces the choice; picking a Retrofit also
// replaces it. The Ascend toggle works by swapping the selectedMachineId
// from Statler-N to Ascend-N (same throat size).

import { useMemo, useState } from "react";

import { SectionFrame } from "./SectionFrame";
import { OptionRow } from "./OptionRow";
import { ProductDetailModal } from "../ProductDetailModal";
import { findFamilyEquivalent, machinesByFamily } from "../../catalog";
import { displayNameFor, imageCandidatesFor } from "../../format";
import type { Product } from "../../api";
import { useConfigurator } from "../../store";
import { useMachineDualPriceLabel } from "../../usePriceLabel";

type TabKey = "statler" | "vision" | "upgrades";

const TAB_LABELS: Record<TabKey, string> = {
  statler: "Statler",
  vision: "Vision",
  upgrades: "Upgrade",
};

export function MachineSection() {
  const { state, dispatch } = useConfigurator();
  const catalog = state.catalog;
  const [detail, setDetail] = useState<Product | null>(null);
  const priceLabelFor = useMachineDualPriceLabel();

  // Tab the currently-selected product belongs to — so revisiting lands the
  // customer on the right tab without manual switching.
  const inferredTab: TabKey | null = useMemo(() => {
    if (!catalog || !state.selectedMachineId) return null;
    const p = catalog.byId.get(state.selectedMachineId);
    if (!p) return null;
    if (p.category === "upgrade") return "upgrades";
    if (p.category === "machine") {
      return p.family === "vision" ? "vision" : "statler";
    }
    return null;
  }, [catalog, state.selectedMachineId]);

  const [activeTab, setActiveTab] = useState<TabKey>(inferredTab ?? "statler");

  // Collapse the machine picker once a machine is chosen so the accessories
  // rise into view. Start collapsed if a machine is already selected (resumed
  // session); "Change" re-expands. Picking a machine auto-collapses.
  const [expanded, setExpanded] = useState(() => !state.selectedMachineId);
  function handleSelect(id: string) {
    dispatch({ type: "machine/select", id });
    setExpanded(false);
  }

  // Keep tab in sync when selection comes from outside (e.g. confirmation reset).
  if (inferredTab && inferredTab !== activeTab && state.selectedMachineId) {
    // Only auto-switch if the user hasn't manually opened a different tab —
    // detected by the inferred tab not matching active. This is a single-shot
    // sync after reset; the natural setState cascade handles further drifts.
  }

  if (!catalog) return null;

  // Pre-loved deep-link: one fixed used machine, read-only. No tabs/selection.
  if (state.preloved) {
    return (
      <SectionFrame number={1} label="Machine" title="Your pre-loved machine">
        <div className="border border-line p-4">
          <div className="font-display text-base font-extrabold uppercase tracking-wide text-ink">
            {state.preloved.description}
          </div>
          <div className="mt-1 font-sans text-sm text-steel">
            Certified pre-loved · fully refurbished · ready to ship
          </div>
          <div className="mt-2 font-display text-lg font-extrabold text-ink">
            {priceLabelFor(state.preloved.priceCents)}
          </div>
        </div>
      </SectionFrame>
    );
  }

  // Collapsed: a compact summary of the chosen machine + a "Change" button. This
  // pulls the accessories section up into view once the machine is picked.
  const selected = state.selectedMachineId
    ? catalog.byId.get(state.selectedMachineId)
    : null;
  if (selected && !expanded) {
    return (
      <SectionFrame number={1} label="Machine" title="Your machine">
        <div className="flex items-center gap-3 border border-line p-3">
          <div className="min-w-0 flex-1">
            <div className="font-display text-base font-extrabold uppercase tracking-wide text-ink">
              {displayNameFor(selected)}
            </div>
            <div className="mt-0.5 font-sans text-sm text-steel">
              {selected.category === "machine" && selected.throatSize
                ? `${selected.throatSize}" throat · `
                : ""}
              {priceLabelFor(selected.price_cents)}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="flex-none font-display text-sm font-extrabold uppercase tracking-wide text-amber transition hover:text-amber-hover"
          >
            Change
          </button>
        </div>
      </SectionFrame>
    );
  }

  return (
    <SectionFrame number={1} label="Machine" title="Choose your machine">
      <div role="tablist" className="flex gap-0 border-b border-line">
        {(Object.keys(TAB_LABELS) as TabKey[]).map((tab) => {
          const isActive = tab === activeTab;
          return (
            <button
              key={tab}
              role="tab"
              type="button"
              aria-selected={isActive}
              onClick={() => setActiveTab(tab)}
              className={
                "flex-1 border-b-2 px-2 py-2.5 font-display text-sm font-extrabold uppercase tracking-wide transition " +
                (isActive
                  ? "border-amber text-ink"
                  : "border-transparent text-steel hover:text-ink")
              }
            >
              {TAB_LABELS[tab]}
            </button>
          );
        })}
      </div>

      {activeTab === "statler" && (
        <StatlerPanel
          selectedId={state.selectedMachineId}
          onSelect={handleSelect}
          onDetail={setDetail}
        />
      )}
      {activeTab === "vision" && (
        <VisionPanel
          selectedId={state.selectedMachineId}
          onSelect={handleSelect}
          onDetail={setDetail}
        />
      )}
      {activeTab === "upgrades" && (
        <UpgradesPanel
          selectedId={state.selectedMachineId}
          onSelect={handleSelect}
          onDetail={setDetail}
        />
      )}
      <ProductDetailModal product={detail} onClose={() => setDetail(null)} />
    </SectionFrame>
  );
}

/* ── Statler tab with Ascend toggle ──────────────────────────────── */

interface PanelProps {
  selectedId: string | null;
  onSelect: (id: string) => void;
  onDetail: (p: Product) => void;
}


function StatlerPanel({ selectedId, onSelect, onDetail }: PanelProps) {
  const { state } = useConfigurator();
  const catalog = state.catalog!;
  const priceLabelFor = useMachineDualPriceLabel();

  // The toggle's "on" state is derived: is the currently-selected machine an
  // Ascend? If so, the customer is viewing the Ascend tier of Statler.
  const selectedMachine = selectedId
    ? catalog.byId.get(selectedId) ?? null
    : null;
  const isAscend =
    selectedMachine?.category === "machine" &&
    selectedMachine.family === "ascend";

  const baseMachines = machinesByFamily(catalog, "statler");
  const ascendMachines = machinesByFamily(catalog, "ascend");
  const rows = isAscend ? ascendMachines : baseMachines;

  function toggleAscend() {
    if (!selectedMachine || selectedMachine.category !== "machine") {
      // No machine selected, or selection isn't in this tab — flip the
      // visual only (no selection change). The next size click will pick
      // the appropriate tier.
      return;
    }
    if (selectedMachine.family !== "statler" && selectedMachine.family !== "ascend") {
      return;
    }
    const targetFamily = isAscend ? "statler" : "ascend";
    const equivalent = findFamilyEquivalent(catalog, selectedMachine, targetFamily);
    if (equivalent) onSelect(equivalent.id);
  }

  return (
    <div className="flex flex-col">
      <AscendToggle
        on={isAscend}
        onChange={toggleAscend}
        // Don't allow toggling unless a Statler/Ascend size is selected,
        // otherwise the click has no effect and is confusing.
        disabled={
          !selectedMachine ||
          selectedMachine.category !== "machine" ||
          (selectedMachine.family !== "statler" &&
            selectedMachine.family !== "ascend")
        }
      />
      <div className="flex flex-col">
        {rows.map((m) => (
          <OptionRow
            key={m.id}
            name={displayNameFor(m)}
            sub={`${m.throatSize}" throat`}
            priceCents={m.price_cents}
            priceLabel={priceLabelFor(m.price_cents)}
            imageCandidates={imageCandidatesFor(m)}
            selected={selectedId === m.id}
            onClick={() => onSelect(m.id)}
            inputType="radio"
            onThumbClick={() => onDetail(m)}
          />
        ))}
      </div>
    </div>
  );
}

function VisionPanel({ selectedId, onSelect, onDetail }: PanelProps) {
  const { state } = useConfigurator();
  const machines = machinesByFamily(state.catalog!, "vision");
  const priceLabelFor = useMachineDualPriceLabel();
  return (
    <div className="flex flex-col">
      {machines.map((m) => (
        <OptionRow
          key={m.id}
          name={displayNameFor(m)}
          sub={`${m.throatSize}" throat`}
          priceCents={m.price_cents}
          priceLabel={priceLabelFor(m.price_cents)}
          imageCandidates={imageCandidatesFor(m)}
          selected={selectedId === m.id}
          onClick={() => onSelect(m.id)}
          inputType="radio"
          onThumbClick={() => onDetail(m)}
        />
      ))}
    </div>
  );
}

function UpgradesPanel({ selectedId, onSelect, onDetail }: PanelProps) {
  const { state } = useConfigurator();
  const upgrades = state.catalog!.upgrades;
  const priceLabelFor = useMachineDualPriceLabel();
  if (upgrades.length === 0) {
    return (
      <p className="py-4 font-sans text-sm text-steel">
        No upgrades available right now. Call 417-256-5919 to discuss your
        existing machine.
      </p>
    );
  }
  return (
    <div className="flex flex-col">
      <p className="border-l-[3px] border-amber pl-4 py-2 font-serif text-[15px] italic leading-relaxed text-graphite">
        Already own a Gammill? Upgrade your existing machine instead of buying
        new.
      </p>
      {upgrades.map((u) => (
        <OptionRow
          key={u.id}
          name={displayNameFor(u)}
          sub="For existing Gammill machines"
          priceCents={u.price_cents}
          priceLabel={priceLabelFor(u.price_cents)}
          imageCandidates={imageCandidatesFor(u)}
          selected={selectedId === u.id}
          onClick={() => onSelect(u.id)}
          inputType="radio"
          onThumbClick={() => onDetail(u)}
        />
      ))}
    </div>
  );
}

function AscendToggle({
  on,
  onChange,
  disabled,
}: {
  on: boolean;
  onChange: () => void;
  disabled: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onChange}
      disabled={disabled}
      aria-pressed={on}
      className={
        "group flex items-center gap-3 border border-dashed py-3 pl-3 pr-4 text-left transition " +
        (on
          ? "border-amber bg-amber/10"
          : disabled
            ? "border-line text-steel"
            : "border-line hover:border-ink")
      }
    >
      <span
        aria-hidden
        className={
          "flex h-5 w-5 flex-none items-center justify-center border-2 transition " +
          (on ? "border-amber bg-amber" : "border-line bg-white")
        }
      >
        {on && (
          <svg
            viewBox="0 0 16 16"
            className="h-3.5 w-3.5"
            fill="none"
            stroke="#1a1a1a"
            strokeWidth="2.5"
            strokeLinecap="square"
          >
            <path d="M3 8.5 L7 12 L13 4" />
          </svg>
        )}
      </span>
      <div className="flex-1">
        <div className="font-sans text-[14px] font-bold uppercase tracking-tag text-ink">
          Upgrade to Statler Ascend{!disabled && " · +$6,000"}
        </div>
        <div className="font-sans text-[14px] text-steel">
          More accuracy, precision, and speed.
        </div>
      </div>
    </button>
  );
}
